import { useEffect, useState } from 'react'
import {
  deleteAiCredential,
  getAiCredentials,
  putAiCredential,
  recheckAiCredential,
  type AiCredential,
  type AiCredentialState,
  type AiProviderInfo,
} from '../lib/api'

/**
 * Bring your own AI key — one per provider.
 *
 * Generation is paid for by the site's key and rationed to twenty a day, which
 * is a cost control standing in for a real one. A learner who brings their own
 * key is spending their own money, so the ceiling becomes theirs.
 *
 * Three providers rather than one because somebody who already pays OpenAI or
 * Google should not have to open an Anthropic account to use a feature they are
 * funding themselves. The list comes from the server, in the order the keys are
 * actually tried, so the sentence about which one pays is not a guess.
 *
 * Three things this screen refuses to do, all deliberate:
 *
 *   * It never shows a stored key. There is no endpoint that returns one — the
 *     last four characters are all that comes back, because an endpoint that
 *     could hand the key over would be the most valuable thing here to attack.
 *   * It never claims a key works without asking. Saving validates against the
 *     provider and shows the answer, including the provider's own words when it
 *     refuses, so "invalid" is never a dead end.
 *   * It never pretends this is private from us. The key is encrypted at rest
 *     under a secret this site holds, which protects the database and not you
 *     from the operator — said plainly below rather than implied.
 */
export function AiKeyCard({ token }: { token: string }) {
  const [state, setState] = useState<AiCredentialState | null>(null)

  useEffect(() => {
    let live = true
    void getAiCredentials(token).then((r) => {
      if (live && r.ok) {
        setState({
          storable: r.storable,
          platformKey: r.platformKey,
          providers: r.providers ?? [],
          credentials: r.credentials ?? [],
        })
      }
    })
    return () => {
      live = false
    }
  }, [token])

  if (!state) return null

  /** Replace one provider's row without disturbing the others. */
  const merge = (provider: string, credential: AiCredential | null) => {
    setState((prev) => {
      if (!prev) return prev
      const rest = prev.credentials.filter((c) => c.provider !== provider)
      return { ...prev, credentials: credential ? [...rest, credential] : rest }
    })
  }

  const valid = state.credentials.filter((c) => c.status === 'valid')
  // Dispatch tries the providers in the order the server sent them, so the
  // first one in that list with a working key is the one that gets billed.
  const paying = state.providers.find((p) => valid.some((c) => c.provider === p.provider))

  return (
    <div className="card">
      <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
        Your own AI key
      </div>

      {!state.storable ? (
        <p className="muted" style={{ fontSize: '0.86rem', margin: 0 }}>
          This site is not set up to store keys yet, so generation runs on the site's own key and
          the daily allowance. Nothing you paste here would be saved.
        </p>
      ) : (
        <>
          <p className="english" style={{ marginTop: 0 }}>
            {valid.length > 0 ? (
              <>
                Generation runs on your <strong>{paying?.label ?? valid[0].provider}</strong> key, at
                whatever your account there allows — the site's daily cap no longer applies.
                {valid.length > 1 && ' Your other keys are spares, used only if that one stops working.'}
              </>
            ) : state.platformKey ? (
              <>
                Generation currently runs on this site's key, capped at twenty a day. Add a key from
                any one of these and the cap becomes your own account's instead.
              </>
            ) : (
              <>This site has no AI key of its own, so generation needs one from you.</>
            )}
          </p>

          {state.providers.map((provider) => (
            <ProviderRow
              key={provider.provider}
              provider={provider}
              token={token}
              credential={state.credentials.find((c) => c.provider === provider.provider) ?? null}
              platformKey={state.platformKey}
              onChange={(cred) => merge(provider.provider, cred)}
            />
          ))}

          <p className="muted" style={{ fontSize: '0.84rem', marginBottom: 0 }}>
            Every key is checked with its provider when you save it, so you find out here rather
            than the next time you press Generate. Stored encrypted, and never shown again — but
            this site holds the key that unseals it, so treat it as protected from a database leak
            rather than from us. Remove any of them at any time.
          </p>
        </>
      )}
    </div>
  )
}

/** One provider: its status, a field, and the two buttons that act on it. */
function ProviderRow({
  provider,
  token,
  credential,
  platformKey,
  onChange,
}: {
  provider: AiProviderInfo
  token: string
  credential: AiCredential | null
  platformKey: boolean
  onChange: (credential: AiCredential | null) => void
}) {
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState<'save' | 'check' | 'remove' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    const typed = key.trim()
    if (typed.length < 16) {
      setError('That does not look like an API key.')
      return
    }
    setBusy('save')
    setError(null)
    const result = await putAiCredential(token, provider.provider, typed)
    setBusy(null)
    if (!result.ok) {
      setError(result.error)
      return
    }
    // Cleared immediately: there is no reason for a secret to sit in a form
    // field after it has been stored.
    setKey('')
    onChange(result.credential)
  }

  const check = async () => {
    setBusy('check')
    setError(null)
    const result = await recheckAiCredential(token, provider.provider)
    setBusy(null)
    if (result.ok) onChange(result.credential)
    else setError(result.error)
  }

  const remove = async () => {
    setBusy('remove')
    setError(null)
    const result = await deleteAiCredential(token, provider.provider)
    setBusy(null)
    if (result.ok) onChange(null)
    else setError(result.error)
  }

  return (
    <div
      style={{
        borderTop: '1px solid var(--line, rgba(0,0,0,0.1))',
        paddingTop: '0.9rem',
        marginTop: '0.9rem',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{provider.label}</strong>
        {credential && (
          <span className={`badge is-${credential.status === 'valid' ? 'automatic' : 'cold'}`}>
            {credential.status}
          </span>
        )}
      </div>

      {error && (
        <div className="notice is-error" style={{ margin: '0.6rem 0' }}>
          {error}
        </div>
      )}

      {credential ? (
        <>
          <p className="english" style={{ margin: '0.4rem 0 0.3rem' }}>
            A key ending <strong>{credential.hint}</strong> is on file, using{' '}
            <code>{credential.model}</code>.
          </p>
          {credential.note && (
            <p className="muted" style={{ fontSize: '0.86rem', marginTop: 0 }}>
              {credential.note}
            </p>
          )}
          {credential.status !== 'valid' && (
            <p className="muted" style={{ fontSize: '0.86rem', marginTop: 0 }}>
              {platformKey
                ? 'This key will be skipped until it works. Generation falls back to your other keys, then to the site’s.'
                : 'This key will be skipped until it works.'}
            </p>
          )}
          <div className="row" style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              className="btn is-small"
              disabled={busy !== null}
              onClick={() => void check()}
            >
              {busy === 'check' ? 'Checking…' : 'Check it again'}
            </button>
            <button
              type="button"
              className="btn is-small is-danger"
              disabled={busy !== null}
              onClick={() => void remove()}
            >
              {busy === 'remove' ? 'Removing…' : 'Remove it'}
            </button>
          </div>
        </>
      ) : (
        <p className="muted" style={{ fontSize: '0.86rem', margin: '0.3rem 0 0' }}>
          No key on file.{' '}
          <a href={provider.keysUrl} target="_blank" rel="noopener noreferrer">
            Get one from {provider.label}
          </a>
          .
        </p>
      )}

      <label className="field" style={{ marginTop: '0.7rem' }}>
        <span className="field-label">
          {credential ? `Replace your ${provider.label} key` : `${provider.label} API key`}
        </span>
        <input
          className="input"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder={provider.placeholder}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save()
          }}
        />
      </label>

      <button
        type="button"
        className="btn is-small is-primary"
        disabled={busy !== null || key.trim().length === 0}
        onClick={() => void save()}
      >
        {busy === 'save' ? 'Saving and checking…' : credential ? 'Replace key' : 'Save key'}
      </button>
    </div>
  )
}
